
import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

interface LiveSyncConfig {
  boardId: string;
  onUpdate?: (data: any) => void;
  enabled?: boolean;
}

export function useLiveSync({ boardId, onUpdate, enabled = true }: LiveSyncConfig) {
  const { user } = useAuth();
  const channelRef = useRef<any>(null);

  useEffect(() => {
    if (!enabled || !boardId || !user) {
      return;
    }

    console.log('🔄 [LIVE SYNC] Setting up live sync for board:', boardId);

    try {
      // Create a unique channel name for this board
      const channelName = `board_sync_${boardId}`;
      
      channelRef.current = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'cards',
            filter: `column_id=in.(select id from columns where board_id=eq.${boardId})`
          },
          (payload) => {
            console.log('🔄 [LIVE SYNC] Card change:', payload);
            onUpdate?.(payload);
          }
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'columns',
            filter: `board_id=eq.${boardId}`
          },
          (payload) => {
            console.log('🔄 [LIVE SYNC] Column change:', payload);
            onUpdate?.(payload);
          }
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'board_activities',
            filter: `board_id=eq.${boardId}`
          },
          (payload) => {
            console.log('🔄 [LIVE SYNC] Activity change:', payload);
            onUpdate?.(payload);
          }
        )
        .subscribe((status) => {
          console.log('🔄 [LIVE SYNC] Channel status:', status);
        });
    } catch (error) {
      console.error('🔄 [LIVE SYNC] Error setting up channel:', error);
    }

    return () => {
      if (channelRef.current) {
        console.log('🔄 [LIVE SYNC] Cleaning up channel');
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [boardId, user, enabled, onUpdate]);

  return {
    isConnected: channelRef.current?.state === 'joined'
  };
}
